import React, { MouseEvent, MouseEventHandler, ReactNode, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Eye, EyeOff } from 'lucide-react';
import { FilledInputProps } from '@mui/material/FilledInput';
import { FormHelperTextProps as FormHelperTextPropsType } from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import { InputProps as StandardInputProps } from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import { InputLabelProps as InputLabelPropsType } from '@mui/material/InputLabel';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';

type Props = {
  name: string;
  autoComplete?: string;
  autoFocus?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  FormHelperTextProps?: Partial<FormHelperTextPropsType>;
  fullWidth?: boolean;
  helperText?: ReactNode;
  InputLabelProps?: Partial<InputLabelPropsType>;
  InputProps?:
    | Partial<StandardInputProps>
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>;
  label?: ReactNode;
  margin?: 'none' | 'dense' | 'normal';
  placeholder?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined' | 'filled';
  onEnter?: MouseEventHandler<HTMLButtonElement>;
};

const HookFormPasswordField = ({
  autoComplete,
  autoFocus= false,
  defaultValue = '',
  disabled= false,
  fullWidth = true,
  helperText='',
  InputLabelProps,
  InputProps,
  label='',
  margin = 'normal',
  name,
  onEnter = () => {},
  placeholder,
  required,
  // No default: an explicit size outranks the theme's MuiTextField defaultProps,
  // so defaulting to 'medium' opted every password field out of the app-wide
  // 38px field scale. Left undefined, the theme decides.
  size,
  variant = 'outlined',
}: Props) => {
  const {
    formState: { errors },
  } = useFormContext();

  const [showPassword, setShowPassword] = React.useState(false);
  const error = errors[name];
  const displayError = Boolean(error?.message);

  const overriddenInputProps = InputProps ?? {};

  const onVisibilityChangePasswordHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setShowPassword(!showPassword);
    },
    [showPassword],
  );

  // The real toggle lives in the adornment. It used to be an EMPTY
  // InputAdornment, with the button absolutely positioned outside the TextField
  // at `top: '25%'` — a guess that only lined up at one field height and drifted
  // as soon as a helperText appeared. Inside the adornment, MUI centres it.
  overriddenInputProps.endAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={onVisibilityChangePasswordHandler}
        edge="end"
        sx={(theme) => ({
          width: 24,
          height: 24,
          color: theme.palette.cx.text.tertiary,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.cx.text.primary,
          },
        })}
      >
        {/* lucide, sized explicitly: a plain <svg> is not MuiSvgIcon, so it does
            not pick up icon sizing from anywhere else. */}
        {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
      </IconButton>
    </InputAdornment>
  );

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default form submission
      onEnter(event);
    }
  };

  const magicId = `${showPassword}_${name}`;
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <TextField
          ref={ref}
          name={name}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          disabled={disabled}
          error={displayError}
          fullWidth={fullWidth}
          helperText={displayError ? String(error.message) : helperText}
          id={magicId}
          slotProps={{
            inputLabel: InputLabelProps,
            input: overriddenInputProps,
          }}
          label={label}
          margin={margin}
          onBlur={onBlur}
          onChange={onChange}
          onKeyUp={handleKeyPress}
          placeholder={placeholder}
          required={required}
          size={size}
          type={showPassword ? 'text' : 'password'}
          value={value ?? ''}
          variant={variant}
        />
      )}
    />
  );
};

export default HookFormPasswordField;
